<template>
    <LoadingCard v-if="loading && !hasLoaded" />
    <div class="content__inner" v-else>
        <header class="content__title pl-0">
            <h1>
                <template v-if="owner">
                    <router-link :to="{name: 'growers.blocks.browse', params: {growerId: owner.id}}">
                        {{ block.name }}
                    </router-link>
                    /
                </template>
                Spray diary
                <small class="pull-right" v-if="block"><strong>Block:</strong> &ldquo;{{ block.name }}&rdquo; ({{ block.variety.name }})</small>
            </h1>
        </header>

        <div class="card">
            <div class="bg-primary align-items-center d-flex">
                <h5 class="card-header text-white">Diary Entries</h5>
                <b-pagination align="right" class="my-auto ml-auto mr-2" :total-rows="pagination.total" v-model="pagination.page"
                              :per-page="pagination.limit" />
                <span v-b-tooltip="dateRange ? 'Download spray logs' : 'Select date range to download spray logs'" v-if="blockId">
                <b-button variant="orange btn--icon mr-2" size="sm" :class="{disabled: !dateRange}"
                          @click="attemptExport">
                    <i class="zmdi zmdi-download"></i>
                </b-button>
                    </span>
            </div>
            <VueDatePicker
                    id="date"
                    :config="dateRangeConfig"
                    v-model="dateRange"
                    ref="dateRangePicker"
                    class="form-control form-control-lg text-center bg-light"
                    placeholder="Click to filter entries by spray date"
                    name="date" />

            <b-table stacked="lg" :items="tableData" :fields="sprayLogsTableFields" :show-empty="true"
                     :sort-by.sync="sprayLogsTable.sortBy"
                     :sort-desc.sync="sprayLogsTable.sortDesc"
                     :no-local-sorting="true"
                     :busy="this.loading > 0">

                <template slot="block" slot-scope="row">
                    {{ row.item.block.name }}
                </template>

                <template slot="variety" slot-scope="row">
                    {{ row.item.block.variety.name }}
                </template>

                <template slot="withholdingPeriods" slot-scope="row">
                    <template v-if="row.item.withholdingPeriods.length > 0">
                    <span class="badge-group mr-2" v-for="period in activeWithholdingPeriods(row.item)">
                        <span class="badge badge-secondary mb-1">{{ period.country.name }} ({{ period.length }} days)</span>
                        <span class="badge mb-1" :class="{'badge-success': period.is_past, 'badge-danger': !period.is_past}">{{ period.ends_at | date_format }}</span>
                    </span>
                    </template>
                    <span class="text-muted" v-else>(none)</span>
                </template>

                <template slot="restrictedEntryInterval" slot-scope="row">
                    <strong class="text-danger" v-if="row.item.restrictedEntryInterval">{{ row.item.restrictedEntryInterval.ends_at | date_format
                                                                                        }}</strong>
                    <span class="text-muted" v-else>(none)</span>
                </template>


                <template slot="sprayed_at" slot-scope="row">
                    <span class="text-nowrap">{{ row.item.sprayed_at | datetime_format }}</span>
                </template>

                <template slot="chemicals" slot-scope="row">
                    <span v-for="entry in [...row.item.entries].sort((a,b) => a.chemical.name.localeCompare(b.chemical.name))"
                          class="badge badge-light mr-1 mb-1">
                        {{ entry.chemical.name }}
                    </span>
                </template>

                <template slot="fertilisers" slot-scope="row">
                    <template v-for="entry in [...row.item.fertiliserEntries].sort((a,b) => a.fertiliser.name.localeCompare(b.fertiliser.name))">
                        <span class="badge badge-light mr-1 mb-1">{{ entry.fertiliser.name }}</span>
                    </template>
                </template>

                <template slot="expand" slot-scope="row">
                    <b-button variant="secondary" size="sm" @click.stop="row.toggleDetails">
                        {{ row.detailsShowing ? 'Hide' : 'Show'}} Details
                    </b-button>
                </template>

                <template slot="row-details" slot-scope="row">
                    <div class="mb-5">
                        <b-button variant="outline-secondary" class="btn-block mb-3" size="sm" v-if="canEdit(row.item)"
                                  :to="{name: 'spray-diary.update', params: {logId: row.item.id}}">
                            <i class="zmdi zmdi-edit"></i> Edit this Diary Entry
                        </b-button>
                        <dl class="row">
                            <dt class="col-6 col-sm-3 col-md-2">Time Taken:</dt>
                            <dd class="col-6 col-sm-3 col-md-2">
                                <template v-if="row.item.time_taken">
                                    {{ row.item.time_taken | minute_format }}
                                </template>
                                <span class="text-muted" v-else>(unknown)</span>
                            </dd>

                            <dt class="col-6 col-sm-3 col-md-2">Wind:</dt>
                            <dd class="col-6 col-sm-3 col-md-2">
                                <template v-if="row.item.wind_direction && row.item.wind_speed">
                                    Speed: {{ windSpeedValues.find(speed => speed.name === row.item.wind_speed).description }},
                                    Direction: {{ windDirectionValues.find(direction => direction.name === row.item.wind_direction).description }}
                                </template>
                                <template v-else-if="row.item.wind_direction">
                                    Direction: {{ windDirectionValues.find(direction => direction.name === row.item.wind_direction).description }}
                                </template>
                                <template v-else-if="row.item.wind_speed">
                                    Speed: {{ windSpeedValues.find(speed => speed.name === row.item.wind_speed).description }}
                                </template>
                                <span class="text-muted" v-else>(unknown)</span>
                            </dd>

                            <dt class="col-6 col-sm-3 col-md-2">Cloud Cover:</dt>
                            <dd class="col-6 col-sm-3 col-md-2">
                                <template v-if="row.item.cloud_cover">
                                    {{ row.item.cloud_cover / 100 | percent_format }}
                                </template>
                                <span class="text-muted" v-else>(unknown)</span>
                            </dd>

                            <dt class="col-6 col-sm-3 col-md-2">Temperature:</dt>
                            <dd class="col-6 col-sm-3 col-md-2">
                                <template v-if="row.item.temperature">
                                    {{ row.item.temperature | number_format }}&deg;c
                                </template>
                                <span class="text-muted" v-else>(unknown)</span>
                            </dd>

                            <dt class="col-6 col-sm-3 col-md-2">Spray Unit:</dt>
                            <dd class="col-6 col-sm-3 col-md-2">
                                <template v-if="row.item.spray_unit_name && row.item.spray_unit_calibrated_on">
                                    {{ row.item.spray_unit_name }} calibrated on {{ row.item.spray_unit_calibrated_on | date_format }}
                                </template>
                                <template v-else-if="row.item.spray_unit_name">
                                    {{ row.item.spray_unit_name }}
                                </template>
                                <span class="text-muted" v-else>(unknown)</span>
                            </dd>

                            <dt class="col-6 col-sm-3 col-md-2">Applicator (Growsafe):</dt>
                            <dd class="col-6 col-sm-3 col-md-2">{{ row.item.growsafe_number }}</dd>

                            <dt class="col-6 col-sm-3 col-md-2">Spray Volume (lt/hec):</dt>
                            <dd class="col-6 col-sm-3 col-md-2">{{ row.item.volume }}</dd>

                            <dd class="col-12" v-if="row.item.entries.length > 0">
                                <b-table small striped stacked="md" :items="row.item.entries" :fields="entriesTable.fields" :show-empty="true">
                                    <template slot="chemical" slot-scope="entry">
                                        {{ entry.item.chemical.name }}
                                    </template>
                                    <template slot="active_ingredients" slot-scope="entry">
                                        {{ entry.item.chemical.activeIngredients.map(ing => ing.activeIngredient.name).join(', ') }}
                                    </template>
                                    <template slot="rate" slot-scope="entry">
                                        {{ entry.item.rate | number_format }}
                                    </template>
                                    <template slot="target" slot-scope="entry">
                                        {{ entry.item.target.name }}
                                    </template>
                                    <template slot="weight" slot-scope="entry">
                                        {{ entry.item.weight | number_format }}
                                    </template>
                                    <template slot="recommended" slot-scope="entry">
                                        {{ (entry.item.recommended || 0).toFixed(2) | number_format }}
                                    </template>
                                </b-table>
                            </dd>

                            <dd class="col-12 col-md-10" v-if="row.item.fertiliserEntries.length > 0">
                                <b-table small striped stacked="md" :items="row.item.fertiliserEntries" :fields="fertiliserEntriesTable.fields"
                                         :show-empty="true">
                                    <template slot="fertiliser" slot-scope="entry">
                                        {{ entry.item.fertiliser.name }}
                                    </template>
                                    <template slot="rate" slot-scope="entry">
                                        {{ entry.item.rate | number_format }}
                                    </template>
                                    <template slot="weight" slot-scope="entry">
                                        {{ entry.item.weight | number_format }}
                                    </template>
                                </b-table>
                            </dd>

                            <dd class="col-12" v-if="withholdingPeriodsByActiveIngredient(row.item).length > 0">
                                <b-table small striped stacked="md" :items="withholdingPeriodsByActiveIngredient(row.item)" :fields="withholdingPeriodsTable.fields" :show-empty="true">
                                    <template slot="active_ingredient" slot-scope="entry">
                                        {{ entry.item.chemical.name }}
                                    </template>
                                    <template slot="withholding_periods" slot-scope="entry">
                                    <span class="badge-group mr-2" v-for="period in entry.item.byCountry">
                                        <span class="badge badge-secondary mb-1">{{ period.country.name }} ({{ period.length }} days)</span>
                                        <span class="badge mb-1" :class="{'badge-success': period.is_past, 'badge-danger': !period.is_past}">{{ period.ends_at | date_format }}</span>
                                    </span>
                                    </template>
                                </b-table>
                            </dd>

                            <dt class="col-8 col-sm-3 col-md-2">Comments:</dt>
                            <dd class="col-8 col-sm-9 col-md-10">
                                <div v-if="row.item.comments" style="white-space: pre-wrap;">{{ row.item.comments }}</div>
                                <span class="text-muted" v-else>(none)</span>
                            </dd>
                        </dl>
                    </div>
                </template>
            </b-table>
        </div>

    </div>
</template>

<script>
import listWindDirectionValuesQuery from '@gql/enumValues/queryWindDirectionEnumValues.graphql';
import listWindSpeedValuesQuery from '@gql/enumValues/queryWindSpeedEnumValues.graphql';

import exportSprayLogMutation from '@views/SprayDiary/View/mutationExportSprayLog.graphql';
import currentUserQuery from '@views/SprayDiary/View/queryCurrentUser.graphql';
import findBlockQuery from '@views/SprayDiary/View/queryFindBlock.graphql';
import listSprayLogsQuery from '@views/SprayDiary/View/queryListSprayLogs.graphql';
import {format as dateFormat, isAfter, subMonths} from 'date-fns';

export default {
  name: 'ViewSprayDiary',
  props: ['blockId', 'startRange'],
  apollo: {
    currentUser: {
      query: currentUserQuery,
      update: data => data.currentUser,
    },
    block: {
      query: findBlockQuery,
      variables() {
        return {
          input: {
            id: this.blockId,
          },
        };
      },
      skip() {
        return !this.blockId;
      },
      update: data => data.findBlock,
    },
    sprayLogs: {
      query: listSprayLogsQuery,
      variables() {
        const variables = {
          input: {
            page: this.pagination.page,
            limit: this.pagination.limit,
            sort: {
              direction: this.sprayLogsTable.sortDesc
                ? 'DESC'
                : 'ASC',
              field: this.sprayLogsTable.sortBy,
            },
          },
        };

        if (this.blockId) {
          variables.input.block_id = this.blockId;
        }

        if (this.hasDateRange) {
          variables.input.sprayed_at_gte = this.startDate;
          variables.input.sprayed_at_lte = this.endDate;
        }

        return variables;
      },
      manual: true,
      result({ data, loading }) {
        if (!loading) {
          this.sprayLogs = data.filterSprayLogs.data;
          this.pagination.page = data.filterSprayLogs.meta.page;
          this.pagination.limit = data.filterSprayLogs.meta.limit;
          this.pagination.total = data.filterSprayLogs.meta.total;
          this.pagination.from = data.filterSprayLogs.meta.from;
          this.pagination.to = data.filterSprayLogs.meta.to;
        }
      },
    },
    windSpeedValues: {
      query: listWindSpeedValuesQuery,
      update: data => data.__type.enumValues,
    },
    windDirectionValues: {
      query: listWindDirectionValuesQuery,
      update: data => data.__type.enumValues,
    },
  },
  watch: {
    loading(newValue) {
      if (!this.hasLoaded && newValue === 0) {
        this.hasLoaded = true;
      }
    },
  },
  data() {
    const end = new Date();
    const start = subMonths(end, 12);
    const lastYearRange = `${dateFormat(start, 'YYYY-MM-DD')} to ${dateFormat(end, 'YYYY-MM-DD')}`;

    return {
      loading: 0,
      hasLoaded: false,
      currentUser: null,
      block: null,
      dateRange: this.startRange ? this.startRange : lastYearRange,
      sprayLogs: [],
      pagination: {
        page: 1,
        limit: this.$tableDefaultPageSize,
        total: 0,
        from: null,
        to: null,
      },
      windDirectionValues: [],
      windSpeedValues: [],
      dateRangeConfig: {
        mode: 'range',
        altInput: true,
        enableTime: false,
        nextArrow: '<i class="zmdi zmdi-long-arrow-right" />',
        prevArrow: '<i class="zmdi zmdi-long-arrow-left" />',
        altFormat: 'M j, Y',
        dateFormat: 'Y-m-d',
      },
      sprayLogsTable: {
        sortBy: 'sprayed_at',
        sortDesc: true,
      },
      entriesTable: {
        fields: [
          { key: 'chemical', label: 'Commercial Product' },
          { key: 'active_ingredients', label: 'Active Ingredients' },
          { key: 'rate', label: 'Rate(mls/gms per 100 lts)' },
          { key: 'target', label: 'Target' },
          { key: 'weight', label: 'Weight(kgs or lts)' },
          { key: 'recommended', label: 'Recommended(l/kg)' },
        ],
      },
      fertiliserEntriesTable: {
        fields: [
          { key: 'fertiliser', label: 'Fertiliser' },
          { key: 'rate', label: 'Rate(mls/gms per 100 lts)' },
          { key: 'weight', label: 'Weight(kgs or lts)' },
        ],
      },
      withholdingPeriodsTable: {
        fields: [
          { key: 'active_ingredient', label: 'Active Ingredient' },
          { key: 'withholding_periods', label: 'Withholding Periods' },
        ],
      },
    };
  },
  computed: {
    isProcessor() {
      return this.currentUser && this.currentUser.role.name === 'processor';
    },
    sprayLogsTableFields() {
      return [
        { key: 'sprayed_at', label: 'Spray Date', sortable: true },
        this.blockId ? false : { key: 'block', label: 'Block' },
        (this.blockId || !this.isProcessor) ? false : { key: 'variety', label: 'Variety', sortable: true },
        this.blockId ? { key: 'withholdingPeriods', label: 'Withholding Periods' } : false,
        this.blockId ? { key: 'restrictedEntryInterval', label: 'REI' } : false,
        { key: 'chemicals', label: 'Commercial Products Used' },
        { key: 'fertilisers', label: 'Fertilisers Used' },
        { key: 'expand', label: ' ' },
      ].filter(Boolean);
    },
    startDate() {
      if (!this.dateRange || !this.dateRange.includes(' to ')) {
        return null;
      }

      return `${this.dateRange.split(' to ')[0]} 00:00:00`;
    },
    endDate() {
      if (!this.dateRange || !this.dateRange.includes(' to ')) {
        return null;
      }

      return `${this.dateRange.split(' to ')[1]} 23:59:59`;
    },
    hasDateRange() {
      return this.startDate && this.endDate;
    },
    owner() {
      if (this.currentUser && this.currentUser.role.name === 'processor') {
        return this.block ? this.block.owner : null;
      }
    },
    tableData() {
      return this.sprayLogs.map(item => ({
        ...item,
        _showDetails: false,
      }));
    },
  },
  methods: {
    withholdingPeriodsByActiveIngredient(item) {
      const byChemical = {};
      item.withholdingPeriods.forEach(period => {
        if (!(period.chemical.id in byChemical)) {
          byChemical[period.chemical.id] = {
            chemical: period.chemical,
            byCountry: {},
          };
        }

        if (!(period.country.id in byChemical[period.chemical.id].byCountry)) {
          byChemical[period.chemical.id].byCountry[period.country.id] = period;
        } else if (isAfter(period.ends_at, byChemical[period.chemical.id].byCountry[period.country.id].ends_at)) {
          byChemical[period.chemical.id].byCountry[period.country.id] = period;
        }
      });
      return Object.values(byChemical);
    },
    canEdit(item) {
      return this.currentUser.role.name === 'grower' || this.currentUser.id === item.creator_id;
    },
    activeWithholdingPeriods(item) {
      const byCountry = {};
      item.withholdingPeriods.forEach(period => {
        if (!(period.country.id in byCountry) || isAfter(period.ends_at, byCountry[period.country.id].ends_at)) {
          byCountry[period.country.id] = period;
        }
      });

      console.log(byCountry);

      return Object.values(byCountry);
    },
    focusDateRangePicker() {
      this.$refs.dateRangePicker.fp.open();
    },
    attemptExport() {
      if (!this.dateRange) {
        return this.focusDateRangePicker();
      }

      this.errors.remove('global');
      this.$validator.validateAll().then(validationWasSuccessful => {
        if (!validationWasSuccessful) {
          console.log('validation failed');
          return;
        }

        const startDate = dateFormat(this.startDate, 'YYYY-MM-DD');
        const endDate = dateFormat(this.endDate, 'YYYY-MM-DD');
        const url = `/${this.currentUser.role.name}/spraylog/${this.blockId}/export-pdf?start_date=${startDate}&end_date=${endDate}`;
        window.open(url, '_blank');
      });
    },
  },
};
</script>
